'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import toast from 'react-hot-toast';

interface DailyBriefing {
  summary: string;
  jobsToday: number;
  weatherRisks: string[];
  openEscalations: number;
  topPriority: string;
}

interface MaterialAlert {
  id: string;
  jobName: string;
  supplier: string;
  material: string;
  deliveryDate: string;
  status: 'confirmed' | 'pending' | 'delayed';
}

interface ChangeOrderApproval {
  id: string;
  jobName: string;
  customer: string;
  amount: string;
  description: string;
  pendingSince: string;
  phone?: string;
}

const DEMO_BRIEFING: DailyBriefing = {
  summary: '',
  jobsToday: 0,
  weatherRisks: [],
  openEscalations: 0,
  topPriority: '',
};

const DEMO_MATERIALS: MaterialAlert[] = [];

const DEMO_CHANGE_ORDERS: ChangeOrderApproval[] = [];

export default function NotificationsAlertsWidget() {
  const [activeTab, setActiveTab] = useState<'briefing' | 'materials' | 'approvals'>('briefing');
  const [briefing, setBriefing] = useState<DailyBriefing | null>(DEMO_BRIEFING);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingRaw, setBriefingRaw] = useState('');
  const [materialAlerts, setMaterialAlerts] = useState<MaterialAlert[]>(DEMO_MATERIALS);
  const [changeOrders, setChangeOrders] = useState<ChangeOrderApproval[]>(DEMO_CHANGE_ORDERS);
  const [dataLoading, setDataLoading] = useState(true);

  const supabase = createClient();

  const loadData = useCallback(async () => {
    setDataLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Load escalations
      const { data: escalations } = await supabase
        .from('escalations')
        .select('id, status')
        .in('status', ['open', 'escalated']);

      // Load materials_alerts
      const { data: materialsAlerts } = await supabase
        .from('materials_alerts')
        .select('id, job_id, job_address, supplier_name, material_description, delivery_status, expected_delivery_date, notes')
        .in('delivery_status', ['confirmed', 'pending', 'delayed'])
        .order('created_at', { ascending: false })
        .limit(10);

      // Load change_orders
      const { data: changeOrdersData } = await supabase
        .from('change_orders')
        .select('id, job_id, job_address, customer_name, description, amount, status, requested_at')
        .eq('status', 'pending_signature')
        .eq('assigned_pm_id', 'pm-john-001')
        .limit(5);

      // Build material alerts from DB or use demo
      if (materialsAlerts && materialsAlerts.length > 0) {
        const alerts: MaterialAlert[] = materialsAlerts.map((ma: any) => ({
          id: ma.id,
          jobName: ma.job_address || ma.job_id || 'Active Job',
          supplier: ma.supplier_name || 'Supplier',
          material: ma.material_description || 'Materials',
          deliveryDate: ma.expected_delivery_date || today,
          status: (ma.delivery_status as 'confirmed' | 'pending' | 'delayed') || 'pending',
        }));
        setMaterialAlerts(alerts);
      }
      // else keep DEMO_MATERIALS

      // Build change orders from DB or use demo
      if (changeOrdersData && changeOrdersData.length > 0) {
        const cos: ChangeOrderApproval[] = changeOrdersData.map((co: any) => ({
          id: co.id,
          jobName: co.job_address ? `${co.job_id} — ${co.job_address}` : `Job #${co.job_id?.slice(0, 6) || 'N/A'}`,
          customer: co.customer_name || 'Customer',
          amount: `$${Number(co.amount || 0).toLocaleString()}`,
          description: co.description || 'Change order pending approval',
          pendingSince: co.requested_at ? new Date(co.requested_at).toLocaleDateString() : 'Today',
        }));
        setChangeOrders(cos);
      }
      // else keep DEMO_CHANGE_ORDERS

      const escalationCount = (escalations || []).length;
      return { jobCount: 0, escalationCount, jobs: [] };
    } catch (err) {
      console.error('NotificationsWidget data load error:', err);
      return { jobCount: 0, escalationCount: 0, jobs: [] };
    } finally {
      setDataLoading(false);
    }
  }, []);

  const generateBriefing = useCallback(async () => {
    setBriefingLoading(true);
    try {
      const { jobCount, escalationCount, jobs } = await loadData();
      const jobList = jobs.slice(0, 5).map((j: any) => `${j.customer_name || 'Customer'} - ${j.job_type || 'Roofing'} (${j.status})`).join(', ');
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

      const response = await getChatCompletion(
        'GEMINI',
        'gemini/gemini-2.5-flash',
        [
          {
            role: 'system',
            content: 'You are a smart project management assistant for a roofing company. Generate a concise morning briefing in JSON format.',
          },
          {
            role: 'user',
            content: `Generate a morning briefing for ${today}. Active jobs: ${jobCount}. Open escalations: ${escalationCount}. Job list: ${jobList || 'No active jobs'}. Include realistic weather risks for roofing work. Return JSON: { "summary": "2-sentence overview", "jobsToday": number, "weatherRisks": ["risk1","risk2"], "openEscalations": number, "topPriority": "one action item" }`,
          },
        ],
        { max_completion_tokens: 400 }
      );

      const content = response?.choices?.[0]?.message?.content || '';
      setBriefingRaw(content);
      try {
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          setBriefing({
            summary: typeof parsed.summary === 'string' ? parsed.summary : String(parsed.summary || ''),
            jobsToday: Number(parsed.jobsToday) || jobCount,
            weatherRisks: Array.isArray(parsed.weatherRisks)
              ? parsed.weatherRisks.map((r: any) => (typeof r === 'string' ? r : typeof r === 'object' && r !== null ? (r.message || r.description || r.type || JSON.stringify(r)) : String(r)))
              : [],
            openEscalations: Number(parsed.openEscalations) || escalationCount,
            topPriority: typeof parsed.topPriority === 'string' ? parsed.topPriority : String(parsed.topPriority || ''),
          });
        }
      } catch {
        setBriefing({
          summary: content,
          jobsToday: jobCount,
          weatherRisks: ['Check forecast before scheduling installs'],
          openEscalations: escalationCount,
          topPriority: 'Review open escalations',
        });
      }
    } catch (err) {
      toast.error('Failed to generate briefing');
    } finally {
      setBriefingLoading(false);
    }
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const statusConfig = {
    confirmed: { label: 'Confirmed', bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircleIcon' },
    pending: { label: 'Pending', bg: 'bg-warning/10', text: 'text-warning', icon: 'ClockIcon' },
    delayed: { label: 'Delayed', bg: 'bg-error/10', text: 'text-error', icon: 'ExclamationTriangleIcon' },
  };

  const tabs = [
    { id: 'briefing', label: 'Daily Briefing', icon: 'SparklesIcon', badge: null },
    { id: 'materials', label: 'Materials', icon: 'TruckIcon', badge: materialAlerts.filter(a => a.status === 'confirmed').length || null },
    { id: 'approvals', label: 'Approvals', icon: 'DocumentCheckIcon', badge: changeOrders.length || null },
  ] as const;

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="BellIcon" size={15} className="text-primary" variant="solid" />
          </div>
          <h3 className="font-heading text-sm font-semibold text-foreground">Notifications & Alerts</h3>
        </div>
        {activeTab === 'briefing' && (
          <button
            onClick={generateBriefing}
            disabled={briefingLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-secondary transition-standard press-scale disabled:opacity-60"
          >
            <Icon name="SparklesIcon" size={12} variant="solid" />
            {briefingLoading ? 'Generating…' : briefing ? 'Refresh' : 'Generate'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium transition-standard relative ${
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary -mb-px' :'text-muted-foreground hover:text-foreground'
            }`}
            style={{ minHeight: '44px' }}
          >
            <Icon name={tab.icon as any} size={13} variant={activeTab === tab.id ? 'solid' : 'outline'} />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.badge ? (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-error text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Daily Briefing Tab */}
        {activeTab === 'briefing' && (
          <>
            {!briefing && !briefingLoading && (
              <div className="flex flex-col items-center justify-center py-8 text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="SparklesIcon" size={22} className="text-primary" variant="outline" />
                </div>
                <div>
                  <p className="font-body text-sm font-medium text-foreground">AI Morning Briefing</p>
                  <p className="font-caption text-xs text-muted-foreground mt-1">
                    Get a smart summary of today's jobs, weather risks, and open escalations
                  </p>
                </div>
                <button
                  onClick={generateBriefing}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary transition-standard press-scale"
                >
                  Generate Briefing
                </button>
              </div>
            )}
            {briefingLoading && (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="font-caption text-xs text-muted-foreground">Generating your morning briefing…</p>
              </div>
            )}
            {briefing && !briefingLoading && (
              <div className="space-y-3">
                {/* Summary */}
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon name="SparklesIcon" size={13} className="text-primary" variant="solid" />
                    <span className="font-caption text-xs font-semibold text-primary uppercase tracking-wide">AI Summary</span>
                  </div>
                  <p className="font-body text-xs text-foreground leading-relaxed">{briefing.summary}</p>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2.5 bg-muted rounded-lg text-center">
                    <p className="font-heading text-lg font-bold text-foreground">{briefing.jobsToday}</p>
                    <p className="font-caption text-[10px] text-muted-foreground">Active Jobs</p>
                  </div>
                  <div className="p-2.5 bg-error/10 rounded-lg text-center">
                    <p className="font-heading text-lg font-bold text-error">{briefing.openEscalations}</p>
                    <p className="font-caption text-[10px] text-muted-foreground">Escalations</p>
                  </div>
                  <div className="p-2.5 bg-warning/10 rounded-lg text-center">
                    <p className="font-heading text-lg font-bold text-warning">{briefing.weatherRisks.length}</p>
                    <p className="font-caption text-[10px] text-muted-foreground">Weather Risks</p>
                  </div>
                </div>

                {/* Weather risks */}
                {briefing.weatherRisks.length > 0 && (
                  <div className="p-3 bg-warning/5 border border-warning/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Icon name="CloudIcon" size={13} className="text-warning" variant="solid" />
                      <span className="font-caption text-xs font-semibold text-warning">Weather Risks</span>
                    </div>
                    <ul className="space-y-1">
                      {briefing.weatherRisks.map((risk, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-warning mt-0.5">•</span>
                          <span className="font-body text-xs text-foreground">{typeof risk === 'string' ? risk : String(risk)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Top priority */}
                {briefing.topPriority && (
                  <div className="p-3 bg-success/5 border border-success/20 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon name="StarIcon" size={13} className="text-success" variant="solid" />
                      <span className="font-caption text-xs font-semibold text-success">Top Priority</span>
                    </div>
                    <p className="font-body text-xs text-foreground">{briefing.topPriority}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Material Arrival Alerts Tab */}
        {activeTab === 'materials' && (
          <>
            {dataLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {materialAlerts.map(alert => {
                  const cfg = statusConfig[alert.status];
                  const isWeatherAlert = alert.material.toLowerCase().includes('weather') ||
                    alert.supplier.toLowerCase().includes('weather') ||
                    (alert as any).notes?.toLowerCase().includes('weather');
                  return (
                    <div key={alert.id} className={`p-3 border rounded-lg hover:border-primary/30 transition-standard ${isWeatherAlert && alert.status === 'delayed' ? 'bg-warning/5 border-warning/30' : 'bg-muted/50 border-border'}`}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {isWeatherAlert && (
                              <Icon name="CloudIcon" size={12} className="text-warning shrink-0" variant="solid" />
                            )}
                            <p className="font-body text-xs font-semibold text-foreground truncate">{alert.jobName}</p>
                          </div>
                          <p className="font-caption text-[11px] text-muted-foreground">{alert.supplier}</p>
                        </div>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.text} shrink-0`}>
                          <Icon name={cfg.icon as any} size={10} variant="solid" />
                          {cfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-1">
                        <span className="flex items-center gap-1">
                          <Icon name="CubeIcon" size={11} variant="outline" />
                          {alert.material}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="CalendarDaysIcon" size={11} variant="outline" />
                          {new Date(alert.deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {materialAlerts.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="TruckIcon" size={28} className="text-muted-foreground mx-auto mb-2" variant="outline" />
                    <p className="font-body text-xs text-muted-foreground">No material deliveries scheduled</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Change Order Approvals Tab */}
        {activeTab === 'approvals' && (
          <>
            {dataLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {changeOrders.map(co => (
                  <div key={co.id} className="p-3 bg-muted/50 border border-warning/30 rounded-lg">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-xs font-semibold text-foreground truncate">{co.jobName}</p>
                        <p className="font-caption text-[11px] text-muted-foreground">{co.customer}</p>
                      </div>
                      <span className="font-heading text-sm font-bold text-warning shrink-0">{co.amount}</span>
                    </div>
                    <p className="font-body text-[11px] text-foreground mb-2 leading-relaxed">{co.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Icon name="ClockIcon" size={10} variant="outline" />
                        Pending {co.pendingSince}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {co.phone && (
                          <a
                            href={`tel:${co.phone}`}
                            className="flex items-center gap-1 px-2 py-1 bg-muted border border-border rounded text-[10px] font-medium text-foreground hover:bg-border transition-standard"
                          >
                            <Icon name="PhoneIcon" size={10} variant="solid" />
                            Call
                          </a>
                        )}
                        <button className="flex items-center gap-1 px-2 py-1 bg-warning/10 border border-warning/30 rounded text-[10px] font-medium text-warning hover:bg-warning/20 transition-standard">
                          <Icon name="PaperAirplaneIcon" size={10} variant="solid" />
                          Send Reminder
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {changeOrders.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="DocumentCheckIcon" size={28} className="text-muted-foreground mx-auto mb-2" variant="outline" />
                    <p className="font-body text-xs text-muted-foreground">No pending change order approvals</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
